import { useEffect, useRef, useState } from 'react';

interface CountOptions {
  duration?: number;
  decimals?: number;
}

export const useCountUp = (
  target: number,
  inView: boolean,
  options: CountOptions = {}
): number => {
  const { duration = 1500, decimals = 0 } = options;
  const [value, setValue] = useState<number>(0);
  const startedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!inView || startedRef.current) return;
    startedRef.current = true;

    const start = performance.now();
    const factor = Math.pow(10, decimals);

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(eased * target * factor) / factor;
      setValue(next);
      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [target, inView, duration, decimals]);

  return value;
};

export const useInView = (
  ref: React.RefObject<HTMLElement>,
  rootMargin: string = '-10% 0px'
): boolean => {
  const [inView, setInView] = useState<boolean>(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin, threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);

  return inView;
};
